import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === "admin" ? "/admin" : redirect);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen pt-[68px] lg:pt-[116px] lg:grid-cols-2" data-testid="login-page">
      <div className="section-dark relative hidden overflow-hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1556817411-31ae72fa3ea0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwcmVjb3Zlcnl8ZW58MHx8fHwxNzg1NjcwNzkyfDA&ixlib=rb-4.1.0&q=85"
          alt="Athlete"
          className="h-full w-full object-cover opacity-50"
        />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-5xl font-extrabold leading-[0.9] tracking-tighter text-white">
            REBUILD<br /><span className="text-[#34D8A0]">STRONGER.</span>
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Welcome back</p>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">Log In</h1>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="login-error">
                {error}
              </div>
            )}
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]"
                data-testid="login-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-glow flex w-full items-center justify-center gap-2 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log In"}
            </button>
          </form>

          <p className="mt-6 text-sm text-black/60">
            No account?{" "}
            <Link to="/register" className="link-underline font-semibold text-black" data-testid="to-register">
              Create one
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
